import { updatePaginationState } from "@/store/paginationStore";
import { Button } from "@/components/ui/button";
import { RefreshCcwIcon } from "lucide-react";
import { PaginatedLiabilityInsuranceQueryVariables, LiabilityInsuranceFilters } from "@/graphql/__generated__/graphql";
import { LiabilityInsuranceDateRange, LiabilityInsuranceSortingDropdown, LiabilityInsuranceValueRange } from "./Components";

interface Props {
  onFilterSubmit: (_variables: PaginatedLiabilityInsuranceQueryVariables) => void;
  displayAmount: number;
}

/**
 * LiabilityInsuranceFilter component handles filtering and sorting of liability insurance records
 * It manages filter state and provides UI for filter controls
 * @param props Component properties
 * @returns JSX element
 */
const LiabilityInsuranceFilter = (props: Props) => {
  const { onFilterSubmit, displayAmount } = props;

  /**
   * Handles filter submission
   * @param newValues New filter values to submit
   */
  const handleSubmitClick = (newValues: PaginatedLiabilityInsuranceQueryVariables) => {
    onFilterSubmit(newValues);
  };

  /**
   * Resets all filter values and sort settings
   * Updates both local state and triggers query update
   */
  const handleResetClickFilters = () => {
    // Define empty filters object
    const emptyFilters = {
      activeFrom: null,
      liabilityInsurance: null,
      professionalAssociation: null,
    };

    // Reset all filter values and sort in the global store
    updatePaginationState({
      paginatedLiabilityInsurance: {
        filters: emptyFilters as LiabilityInsuranceFilters,
        sort: null
      }
    });

    // Create reset query variables with explicitly empty filters and sort
    const updatedVariables: PaginatedLiabilityInsuranceQueryVariables = {
      first: displayAmount,
      filters: {
        activeFrom: null,
        liabilityInsurance: null,
        professionalAssociation: null,
      },
      sort: null
    };

    // Immediately submit the reset filter state to parent component
    onFilterSubmit(updatedVariables);
  };

  return (
    <div className="flex flex-1 gap-2">
      <div className="flex flex-1 items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Date Range - Controls activeFrom date filter */}
          <LiabilityInsuranceDateRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Value Range - Controls liabilityInsurance and professionalAssociation value filters */}
          <LiabilityInsuranceValueRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Sorting Dropdown - Controls sort field and direction */}
          <LiabilityInsuranceSortingDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />
        </div>
        <div>
          <Button variant="outline" size="icon" onClick={handleResetClickFilters}>
            <RefreshCcwIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LiabilityInsuranceFilter;
